import { Injectable, Injector, InjectionToken, NgZone, OnDestroy, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  BehaviorSubject, Observable, Subject, of, defer, throwError, firstValueFrom,
  tap, switchMap, catchError, map, finalize, shareReplay, timeout, defaultIfEmpty,
} from 'rxjs';
import { SignalrService } from './signalr.service';
import { LockStateService } from './lock-state.service';
import { NetworkGuardService } from './network-guard.service';
import { DialogService } from './dialog.service';
import { ToastService } from './toast.service';
import { ApiService } from './api.service';
import { OfflineCacheService } from './offline-cache.service';
import { SW_CACHE_STORAGE, purgeSwDataCaches } from '../utils/sw-cache-guard';
import { environment } from '../../../environments/environment';
import {
  IDLE_TIMEOUT_MS, WARNING_MS, HEARTBEAT_MS, REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS, REFRESH_JITTER_MS,
  TRANSIENT_RETRY_MS, REFRESH_TIMEOUT_MS, ACTIVITY_THROTTLE_MS, HOUSEKEEP_AFTER_MS, PEER_SYNC_WAIT_MS, SUSPENDED_GAP_MS,
  OWNER_AUTH_REQUEST, NGSW_BYPASS, SHARED_PC_UI_CACHE_KEYS, AUTH_KEY, OWNER_STASH_KEY, LOCK_KEY, TAB_SEEN_KEY, LOGOUT_REASON_KEY, activityKey, signedOutKey,
  CHANNEL, sessionChannelName, LEGACY_LOGOUT, OTHER_TAB_TEXT, SUPPORT_ENDED_TEXT,
  ChannelLike, Identity, StartupDecision, UserLogoutMsg, SessionMsg,
  jwtExpMs, jwtSid, tokenIdentity, sameIdentity, localExpiryMs, tokenExpMs, readNum, parseSignedOut, readSignedOut,
  decideStartup, shouldHonourUserLogout, classifyRefreshError, transientRetryMs, reasonFor, saveReason,
  ACCOUNT_SETUP_INVALID, ACCOUNT_SETUP_INVALID_TEXT, ROLE_HOME, isKnownRole, LogoutReason, logoutReasonFor,
} from './session-policy';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface CurrentUser {
  token: string;
  /** Body mode only, and never ''. In cookie mode the refresh token lives only in the HttpOnly cookie (TC-0061). */
  refreshToken?: string;
  fullName: string;
  email: string;
  role: string;
  department: string | null;
  staffId: number;
  hospitalId: number;
  hospitalName: string;
  hospitalLogo: string | null;
  expiry: string;
  mustChangePassword?: boolean;
  /** Platform-owner access state of the hospital: 'Active' | 'ReadOnly' (a suspended hospital can't sign in). */
  hospitalAccess?: string;
  hospitalAccessReason?: string | null;
  /** Set only while the platform owner works inside a hospital through hospital-approved support access. */
  support?: SupportInfo;
  /** This device session (server StaffSessions.Id, 32 hex) — data.sessionId, else the JWT 'sid' claim. */
  sessionId?: string | null;
  /** When this tab signed in (ms). Start-up compares it with the person's sign-out marker in this browser. */
  signedInAt?: number;
  /** Local expiry (ms) = time received + token lifetime. Immune to device clock and time-zone skew (TC-0159). */
  expLocalMs?: number | null;
}

export interface SupportInfo {
  grantId: number;
  hospitalId: number;
  hospitalName: string;
  /** 'ViewOnly' | 'FullAccess' — what the hospital allowed. */
  scope: string;
  /** When the hospital's approval runs out. */
  endsAt: string;
}

export type RefreshTrigger = 'timer' | '401' | 'startup' | 'unlock';
export type RefreshResult =
  | { ok: true; user: CurrentUser }
  | { ok: false; terminal: boolean; status?: number; code?: string; message?: string; error?: unknown };
export type EndKind = 'user' | 'everywhere' | 'idle' | 'forced' | 'remote' | 'stale';
export interface EndOptions {
  kind: EndKind;
  /** Server code (SESSION_REVOKED, HOSPITAL_SUSPENDED, …) — picks the sign-in page text. */
  code?: string;
  message?: string | null;
  /** TC-0228: TOKEN_REVOKED's revoke reason (RefreshReuse, TokenMismatch, Replaced…) — also picks the sign-in page text. */
  reason?: string;
  /** For kind 'remote': the reason the other tab gave ('user' | 'everywhere' | 'idle' | 'forced'). */
  remoteReason?: string;
  /** Send POST /auth/logout for this tab's session. Default: every kind except 'everywhere'. */
  serverLogout?: boolean;
  /** Default: user/everywhere/idle → 'user'; forced → 'session'; remote/stale → 'none'. */
  broadcast?: 'user' | 'session' | 'none';
  /** Default true: go to /login. Start-up endings leave the first navigation to AuthGuard. */
  navigate?: boolean;
  /** skip the support-tab return-to-owner step: full sign-out of this device */
  fromSupportExit?: boolean;
}

/** cookie mode only when the API host equals the page host, so a production build that still uses environment.ts can
 *  never switch it on cross-site */
export const AUTH_REFRESH_MODE = new InjectionToken<'body' | 'cookie'>('AUTH_REFRESH_MODE', {
  providedIn: 'root',
  factory: () => {
    const env = environment as { apiUrl: string; refreshCookieMode?: boolean };
    if (env.refreshCookieMode !== true) return 'body';
    try { return new URL(env.apiUrl, location.href).hostname === location.hostname ? 'cookie' : 'body'; }
    catch { return 'body'; }
  },
});

/** Opens a cross-tab channel by name; specs inject a fake hub. Null where BroadcastChannel is missing. */
export const AUTH_CHANNEL_FACTORY = new InjectionToken<(name: string) => ChannelLike | null>('AUTH_CHANNEL_FACTORY', {
  providedIn: 'root',
  factory: () => (name: string) => (typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(name) as unknown as ChannelLike : null),
});

/**
 * AuthService — this tab's sign-in, as ONE session state machine (C1, 2026-10-01):
 *  - one renewal at a time per tab (refreshSession), through an interceptor-free HttpClient;
 *  - every sign-out goes through endSession(): server logout by keepalive fetch, sign-in page reason, scoped
 *    cross-tab message, local teardown;
 *  - idle time is shared per person across this browser's tabs (localStorage mt_act_<staffId>);
 *  - start-up is decided by restoreOnStartup() from the app initializer, never from the constructor.
 * The pure rules live in session-policy.ts.
 */
@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  /** sessionStorage key read once by LoginComponent. */
  static readonly SIGN_OUT_REASON_KEY = LOGOUT_REASON_KEY;

  private apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  // ── Idle session timeout (HMS-grade). 15 min total: at 14 min we show a 60-second countdown warning; if no
  // interaction, endSession({kind:'idle'}) fires. The idle clock is shared by the person's tabs in this browser.
  // Server-side middleware mirrors this so a stolen token also stops working at 15 min idle.
  public sessionTimeoutWarning$ = new BehaviorSubject<number | null>(null); // Countdown seconds (null = hidden)
  /** A tab restored from the back/forward cache after the restore window — LayoutComponent shows the lock screen. */
  public readonly lockRequested$ = new Subject<void>();

  private readonly refreshMode = inject(AUTH_REFRESH_MODE);
  private readonly channelFactory = inject(AUTH_CHANNEL_FACTORY);
  private readonly zone = inject(NgZone);
  // same pattern as JourneyService:35 — refresh never passes through the app interceptors (DISC-002/DISC-007)
  private readonly bareHttp = new HttpClient(inject(HttpBackend));
  private readonly swCaches = inject(SW_CACHE_STORAGE);

  // ── Cross-tab channels ──────────────────────────────────────────
  // 'mt-auth' (shared): user-scope logouts {v:2, staffId} only — no sid, no tokens.
  // 'mt-auth:<sid>' (per session): session-scope logouts and 'rotated' tokens; only tabs holding that session open it.
  private sharedChannel: ChannelLike | null = null;
  private sessionChannel: ChannelLike | null = null;
  private sessionChannelSid: string | null = null;

  // ── Renewal (single flight) ─────────────────────────────────────
  private refreshInFlight$: Observable<RefreshResult> | null = null;
  private flightSeq = 0;
  private currentFlightId = 0;
  /** Set when a sign-out happened while a renewal was in flight: its late token is used once to log out (DISC-009). */
  private lateLogoutFlightId: number | null = null;
  /** TC-0114: the reason that sign-out sends, so the late token's logout carries it too. */
  private lateLogoutReason: LogoutReason | undefined = undefined;
  private refreshTimer: any = null;
  private refreshRetryTimer: any = null;
  private refreshWhenOnline = false;
  /** The last transient renewal failure; waitMs is its back-off (30 s, or a 429's Retry-After). */
  private lastTransient: { at: number; result: RefreshResult; waitMs?: number } | null = null;

  // ── Peer sync (body mode) ───────────────────────────────────────
  // A tab that was not running while another tab of its session rotated (reopened with Ctrl+Shift+T, discarded and
  // reloaded, restored from the back/forward cache, unfrozen, or a renewal timer that fired very late) still holds
  // the old pair in sessionStorage. Renewing with it would be S1 reuse and end the shared session, so it first asks
  // the live tabs ('sync') and adopts their newer pair.
  private peerSyncNeeded = false;
  private peerSync$: Observable<void> | null = null;
  private readonly peerAdopted$ = new Subject<void>();

  // ── Server logouts that failed on the network: MEMORY ONLY, re-sent on 'online', every 30 s and on pagehide ──
  private pendingLogouts: { token: string; dropAt: number; reason?: LogoutReason }[] = [];
  private pendingFlushTimer: any = null;

  // ── Idle tracking ───────────────────────────────────────────────
  private inactivityTimeout: any = null;
  private warningTimeout: any = null;
  private warningTickHandle: any = null;
  private watchdog: any = null;
  private lastActivityMs = 0;
  private lastActivityWriteMs = 0;
  private ending = false;
  /**
   * Monotonically-increasing session generation. Bumped on every sign-in, every applied renewal or adopted
   * rotation, and FIRST on every sign-out. Used to detect "stale" async callbacks — a renewal that started under
   * epoch N and comes back while we're now in epoch N+1 must neither sign anyone out nor re-store a session.
   *
   * Bug history: app-boot silent-refresh of an expired-RT session was firing
   * while the user was already submitting a fresh login. The refresh failed
   * (old RT revoked), its error callback ran logoutSync() AFTER the new
   * tokens had been stored — user saw login → instant logout. Captured epoch
   * comparison closes the race. DISC-009: the same check now also stops a late
   * renewal from putting a signed-out session back.
   */
  private sessionEpoch = 0;

  // ✅ USER ACTIVITY TRACKING (prevents background polling from extending session)
  private activityEvents = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'] as const;
  private activityListenerAttached = false;
  private readonly listeners: Array<[EventTarget, string, EventListener]> = [];

  // Lazy-inject SignalrService via Injector to avoid potential circular DI
  private _signalrService: SignalrService | null = null;
  private get signalrService(): SignalrService {
    if (!this._signalrService) {
      this._signalrService = this.injector.get(SignalrService);
    }
    return this._signalrService;
  }

  // ── AUTH STORAGE ────────────────────────────────────────────────
  // We use sessionStorage (not localStorage) so the credential is bounded
  // to the lifetime of the browser tab/window. Closing the browser → no
  // session for the next user on the same machine. This blocks the
  // "doctor closes browser, next doctor opens app and inherits session"
  // privacy/audit-trail leak. localStorage holds only staff ids and times
  // (mt_act_<id>, mt_signout_<id>), never a token. Keys: session-policy.ts.
  private exitingSupport = false;
  private mustChangeRecheck$: Observable<boolean> | null = null;

  constructor(private http: HttpClient, private router: Router, private injector: Injector) {
    // One-time migration: purge any leftover legacy localStorage credential
    // so users upgrading from the localStorage-era don't carry a stale token.
    try { localStorage.removeItem(AUTH_KEY); } catch {}

    // No storage decision and NO HTTP in this constructor: HttpClient builds its interceptor chain on the first
    // request and that chain injects AuthService/JourneyService — the NG0200 cycle behind DISC-007.
    // restoreOnStartup() runs from the app initializer.
    if (typeof window === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      this.sharedChannel = this.channelFactory(CHANNEL);
      if (this.sharedChannel) this.sharedChannel.onmessage = e => this.handleSharedMessage(e.data);
      this.listen(window, 'storage', e => this.onStorage(e as StorageEvent));
      this.listen(window, 'online', () => this.onOnline());
      this.listen(window, 'pagehide', () => { this.writeTabSeen(); this.flushPendingLogouts(true); });
      this.listen(document, 'visibilitychange', () => document.visibilityState === 'hidden' ? this.writeTabSeen() : this.checkIdleNow());
      this.listen(window, 'focus', () => this.checkIdleNow());
      this.listen(window, 'pageshow', e => { if ((e as PageTransitionEvent).persisted) this.onPageRestored(); });
      this.listen(document, 'freeze', () => this.writeTabSeen());
      // Unfrozen: rotations by other tabs may have been missed while frozen.
      this.listen(document, 'resume', () => { this.checkIdleNow(); this.requestPeerSync(); });
    });
  }

  ngOnDestroy(): void {
    try { this.sharedChannel?.close(); } catch {}
    this.sharedChannel = null;
    this.closeSessionChannel();
    for (const [target, type, fn] of this.listeners) { try { target.removeEventListener(type, fn); } catch {} }
    this.listeners.length = 0;
    this.stopSessionTracking();
    this.clearRefreshTimers();
    this.stopPendingFlush();
  }

  private listen(target: EventTarget, type: string, fn: (e: Event) => void): void {
    const f = fn as EventListener;
    target.addEventListener(type, f);
    this.listeners.push([target, type, f]);
  }

  // ── START-UP (app initializer) ──────────────────────────────────
  /**
   * Decides, once per page load, whether this tab's stored sign-in ends, resumes, or resumes behind the lock screen
   * (TC-0004). An expired token is renewed BEFORE the first screen renders (boot splash showing). Never throws.
   */
  public async restoreOnStartup(): Promise<void> {
    this.purgeSavedApiReplies();   // DISC-012: never awaited, boot must not wait on Cache Storage
    try {
      let u: CurrentUser | null = null;
      let raw: string | null = null;
      try { raw = sessionStorage.getItem(AUTH_KEY); } catch {}
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (!parsed || typeof parsed !== 'object' || typeof parsed.token !== 'string') throw new Error('bad user');
          u = parsed as CurrentUser;
        } catch {
          try { sessionStorage.removeItem(AUTH_KEY); } catch {}
          this.setLocked(false);
          return;
        }
      }
      if (!u) { try { sessionStorage.removeItem(TAB_SEEN_KEY); } catch {} return; }

      const now = Date.now();
      const d = this.startupDecision(u, (document as any).wasDiscarded === true, now);
      this.currentUserSubject.next(u);   // endSession needs the token and sid

      // Endings never navigate: AuthGuard sends the first navigation to /login?returnUrl=… and LoginComponent
      // shows the reason.
      if (d === 'end-signed-out') { this.endSignedOutInBrowser(u, false); return; }
      if (d === 'end-idle') { this.endSession({ kind: 'idle', broadcast: 'none', navigate: false }); return; }
      if (d === 'end-stale') { this.endSession({ kind: 'stale', broadcast: 'none', navigate: false }); return; }

      // LayoutComponent reads the flag in ngOnInit; L1's lockedRouteGuard keeps /change-password and /tele behind it.
      if (d === 'resume-locked') this.setLocked(true);

      if (u.support && (d === 'resume-locked' || tokenExpMs(u) <= now)) {
        // The lock flag survives the reload into the owner console.
        const msg = d === 'resume-locked' ? 'Your support session ended when the tab was closed.' : 'Your support session timed out.';
        setTimeout(() => this.exitSupportSession(msg));
        return;
      }

      // Resume. The idle clock does NOT restart.
      this.lastActivityMs = readNum(this.local, activityKey(u.staffId)) ?? now;
      this.writeTabSeen();
      this.startSessionTracking();
      this.syncSessionChannel();
      // A reopened, reloaded or discarded tab may hold a pair another tab has rotated since: ask the live tabs now. The
      // first renewal (the blocking one below, or the timer's) waits for the answer.
      this.requestPeerSync();

      if (tokenExpMs(u) - now > 60_000) { this.scheduleTokenRefresh(u); return; }
      if (!navigator.onLine) { this.refreshWhenOnline = true; return; }
      // BLOCKS bootstrap behind the index.html boot splash (bounded by REFRESH_TIMEOUT_MS), so no screen, poll or
      // SignalR start ever sees the expired token. A terminal result has already ended the session (no navigation);
      // a transient one keeps the session and schedules a retry.
      await firstValueFrom(this.refreshSession('startup'), { defaultValue: { ok: false, terminal: false } as RefreshResult });
    } catch (e) {
      console.error('Start-up session restore failed', e);
      try { this._localOnlyLogout(); } catch {}
    }
  }

  /**
   * Start-up / back-forward-cache rule (1): the person signed out in this browser after this tab signed in. The
   * sign-in page shows the reason that sign-out gave (its mt_signout_<id> marker: 'idle', 'everywhere' or 'user'),
   * exactly as a live tab is told over the channel — not always 'You signed out in another tab.' (contract O / P1).
   */
  private endSignedOutInBrowser(u: CurrentUser, navigate: boolean): void {
    const reason = readSignedOut(this.local, u.staffId)?.reason ?? 'user';
    this.endSession({
      kind: 'remote', remoteReason: reason, message: OTHER_TAB_TEXT,
      serverLogout: reason !== 'everywhere', broadcast: 'none', navigate,
    });
  }

  private startupDecision(u: CurrentUser, discarded: boolean, now = Date.now()): StartupDecision {
    let tabSeen: number | null = null;
    try { tabSeen = readNum(sessionStorage, TAB_SEEN_KEY); } catch {}
    return decideStartup({
      nowMs: now,
      tabSeenAtMs: tabSeen,
      sharedActivityMs: readNum(this.local, activityKey(u.staffId)),
      signedOutAtMs: readSignedOut(this.local, u.staffId)?.at ?? null,
      signedInAtMs: u.signedInAt ?? null,
      tokenExpMs: tokenExpMs(u),
      discarded,
    });
  }

  // ── LOGIN ─────────────────────────────────
  login(request: LoginRequest): Observable<any> {
    // X-Retry: handle Azure cold-start 504/timeouts by retrying the POST
    // TC-0231 / TC-0232: X-No-Toast — the login page shows every refusal once, in its own red box (the global pop-up
    // added the framework's "One or more validation errors occurred."). A dropped connection still gets the network
    // toast from AuthInterceptor.
    const h: Record<string, string> = { 'X-Retry': 'true', 'X-Timeout': '60000', 'X-No-Toast': 'true' };
    if (this.refreshMode === 'cookie') h['X-Refresh-Mode'] = 'cookie';
    const headers = new HttpHeaders(h);
    return this.http.post<any>(`${this.apiUrl}/auth/login`, request, { headers }).pipe(
      tap(response => {
        // MFA step 1 returns { mfaRequired: true } with NO token — don't establish a
        // session yet; the login component collects the OTP and calls verifyMfa().
        if (response.success && response.data && !response.data.mfaRequired) {
          this.establishSession(response.data);
        }
      })
    );
  }

  // ── MFA step 2: verify the emailed OTP, then establish the session ──
  // DISC-031: noToast for an owner's recovery code — the login page shows that refusal itself, in one sentence.
  verifyMfa(staffId: number, sessionToken: string, otpCode: string, noToast = false): Observable<any> {
    const h: Record<string, string> = {};
    if (this.refreshMode === 'cookie') h['X-Refresh-Mode'] = 'cookie';
    if (noToast) h['X-No-Toast'] = 'true';
    const options = Object.keys(h).length ? { headers: h } : {};
    return this.http.post<any>(`${this.apiUrl}/auth/verify-mfa`, { staffId, sessionToken, otpCode }, options).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.establishSession(response.data);
        }
      })
    );
  }

  // ── MFA first-time setup (DISC-015): a setup code unlocks the QR. Never a session: the reply swaps the step-1 token
  // for an 'e:' enrolment token, which verifyMfa() then uses with the first authenticator code. ──
  verifyMfaSetupCode(staffId: number, sessionToken: string, setupCode: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/mfa-setup/verify`, { staffId, sessionToken, setupCode });
  }

  /** Emails a setup code to the LOGIN address only (where SMTP is configured). Always 200 unless the sign-in must restart. */
  emailMfaSetupCode(staffId: number, sessionToken: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/mfa-setup/email-code`, { staffId, sessionToken });
  }

  // ── MFA: resend the OTP (60s cooldown enforced server-side) ──
  // DISC-016: bound to this sign-in's step-1 token; the login page shows every server reply itself. X-No-Toast is
  // stripped by ErrorInterceptor and X-Timeout by TimeoutInterceptor, so neither reaches the API or CORS.
  resendMfa(staffId: number, sessionToken: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/resend-mfa`, { staffId, sessionToken },
      { headers: new HttpHeaders({ 'X-No-Toast': 'true', 'X-Timeout': '45000' }) });
  }

  /** Store the authenticated user + start session/refresh timers. Shared by login() and verifyMfa(). */
  private establishSession(data: any): void {
    // P1: an old reason never shows after a successful sign-in.
    try { sessionStorage.removeItem(LOGOUT_REASON_KEY); } catch {}
    // Bump the session epoch BEFORE storing new tokens so any in-flight renewal from the previous session
    // sees a stale epoch and is ignored.
    this.sessionEpoch++;
    this.refreshInFlight$ = null;
    this.lastTransient = null;
    this.peerSyncNeeded = false;   // a fresh sign-in holds the newest pair
    this.peerSync$ = null;
    this.clearRefreshTimers();
    const user = this.normalise(data, null);
    this.persist(user);
    this.currentUserSubject.next(user);
    // a real password/MFA sign-in proves who is at the screen; no lock carries over (TC-0168/TC-4555)
    this.setLocked(false);
    // DISC-038: a fresh sign-in may be a different person on a shared device. Wipe any offline PHI cache
    // a previous session left behind if its sign-out wipe never ran (e.g. a crash). Fire-and-forget.
    try { this.zone.runOutsideAngular(() => { void this.injector.get(OfflineCacheService).clearAllData(); }); } catch {}
    this.recordActivity(Date.now());
    this.writeTabSeen();
    this.startSessionTracking();
    this.syncSessionChannel();
    this.scheduleTokenRefresh(user);
    this.housekeep();
  }

  /** Shapes a login/refresh reply into the stored user (refresh token only in body mode, never ''; sid; local expiry). */
  private normalise(data: any, prev: CurrentUser | null): CurrentUser {
    const u: CurrentUser = { ...data };
    if (this.refreshMode === 'cookie' || !u.refreshToken) {
      if (prev?.refreshToken && this.refreshMode === 'body') u.refreshToken = prev.refreshToken;
      else delete u.refreshToken;
    }
    u.sessionId = data?.sessionId
      ?? jwtSid(data?.token)
      ?? (prev && String(prev.staffId) === String(data?.staffId) ? prev.sessionId ?? null : null);
    u.signedInAt = prev?.signedInAt ?? Date.now();
    u.expLocalMs = localExpiryMs(data?.token, Date.now());
    return u;
  }

  private persist(u: CurrentUser): void {
    try { sessionStorage.setItem(AUTH_KEY, JSON.stringify(u)); } catch {}
  }

  /** localStorage, or null where the browser blocks it. */
  private get local(): Storage | null {
    try { return localStorage; } catch { return null; }
  }

  private readStash(): CurrentUser | null {
    try {
      const raw = sessionStorage.getItem(OWNER_STASH_KEY);
      return raw ? JSON.parse(raw) as CurrentUser : null;
    } catch { return null; }
  }

  /** This device session's id (null in a support tab — support tokens have no sid). */
  get sessionId(): string | null {
    const u = this.currentUser;
    return u && !u.support ? (jwtSid(u.token) ?? u.sessionId ?? null) : null;
  }

  /** The per-session channel this tab belongs to: a support tab joins its owner's session. */
  private get channelSid(): string | null {
    const u = this.currentUser;
    if (!u) return null;
    if (u.support) {
      const stash = this.readStash();
      return stash ? (stash.sessionId ?? jwtSid(stash.token)) : null;
    }
    return this.sessionId;
  }

  /** In cookie mode the SERVER decides (no sessionId requirement, so no deploy-order coupling with S1). */
  get canRefresh(): boolean {
    const u = this.currentUser;
    if (!u) return false;
    if (u.support) return true;
    return this.refreshMode === 'cookie' ? true : !!u.refreshToken;
  }

  /** True when `sentToken` belongs to the same person, device session and support grant as this tab's token. */
  public isCurrentIdentity(sentToken: string): boolean {
    const cur = this.currentUser;
    return !!cur && sameIdentity(tokenIdentity(sentToken), tokenIdentity(cur.token));
  }

  // ── LOGOUT ────────────────────────────────
  logout(): Observable<any> {
    // In a support session "sign out" means "leave the hospital and go back to the owner console" — the server
    // refuses a real sign-out from a support token (it would sign the owner out everywhere).
    if (this.inSupportSession) { this.exitSupportSession(); return of({ success: true }); }
    // Always succeeds locally; the server logout is sent by endSession (keepalive fetch, queued when offline).
    this.endSession({ kind: 'user' });
    return of({ success: true });
  }

  /** @deprecated use endSession(). Kept for callers that sign the user out themselves. */
  logoutSync(): void {
    this.endSession({ kind: 'user' });
  }

  /** "Sign out everywhere" (S1): ends every session of this person on the server, then this tab. */
  logoutEverywhere(): Observable<any> {
    if (this.inSupportSession) { this.exitSupportSession(); return of({ success: true }); }
    this.clearRefreshTimers();
    return this.http.post<any>(`${this.apiUrl}/auth/logout-all`, {}).pipe(
      tap(() => this.endSession({ kind: 'everywhere', serverLogout: false })),
      catchError(err => {
        const cu = this.currentUser;
        if (cu) this.scheduleTokenRefresh(cu);
        return throwError(() => err);
      })
    );
  }

  /** The session can't continue (renewal failed / approval ended): support → back to the owner; otherwise sign out. */
  handleSessionLost(message?: string): void {
    this.endSession({ kind: 'forced', message });
  }

  /** P1 compatibility: a sign-out the person did not choose, with the reason taken from a server reply. */
  forceSignOut(source?: unknown): void {
    const { code, message, reason } = AuthService.reasonSource(source);
    this.endSession({ kind: code === 'IDLE_TIMEOUT' ? 'idle' : 'forced', code, message, reason });
  }

  /** P1 compatibility: saves the sign-in page reason for a server reply without signing out. */
  rememberSignOutReason(source: unknown): void {
    const { code, message, reason } = AuthService.reasonSource(source);
    const r = reasonFor('forced', { code, message, reason });
    if (r && (code || /suspended/i.test(message ?? ''))) saveReason(r);
  }

  private static reasonSource(source: unknown): { code?: string; message?: string; reason?: string } {
    const body: any = source instanceof HttpErrorResponse ? source.error : source;
    if (!body || typeof body !== 'object') return {};
    return {
      code: typeof body.code === 'string' ? body.code : undefined,
      message: typeof body.message === 'string' ? body.message : undefined,
      reason: typeof body.reason === 'string' ? body.reason : undefined,   // TC-0228
    };
  }

  /**
   * THE one path for every sign-out (Logout, idle, refused renewal, server refusal, other tab, start-up). In order:
   * bump the epoch (a late renewal is then ignored), stop timers, send the server logout, save the sign-in page
   * reason, post the scoped cross-tab message, tear down locally, go to /login.
   */
  public endSession(o: EndOptions): void {
    const run = () => this.endSessionNow(o);
    if (NgZone.isInAngularZone()) run(); else this.zone.run(run);
  }

  private endSessionNow(o: EndOptions): void {
    if (this.ending && !o.fromSupportExit) return;
    const wasEnding = this.ending;
    this.ending = true;
    try {
      const serverLogout = o.serverLogout ?? o.kind !== 'everywhere';
      const broadcast = o.broadcast
        ?? (o.kind === 'user' || o.kind === 'everywhere' || o.kind === 'idle' ? 'user' : o.kind === 'forced' ? 'session' : 'none');
      const navigate = o.navigate ?? true;

      // A support exit is in progress and its finish() decides. (A 401 during 'End support' used to fall through
      // to a full sign-out with the owner's token.)
      if (this.exitingSupport && !o.fromSupportExit) return;

      const u = this.currentUser;
      if (!u) { this._localOnlyLogout(); return; }   // idempotent, never broadcasts twice

      if (u.support && o.kind === 'forced' && !o.fromSupportExit && this.readStash()) {
        this.exitSupportSession(o.message || SUPPORT_ENDED_TEXT);
        return;
      }

      // Capture BEFORE clearing. Support tokens are refused at /auth/logout: a support tab logs out the owner's own
      // sign-in on this device.
      const chanSid = this.channelSid;
      const logoutToken = u.support ? (this.readStash()?.token ?? null) : u.token;
      const lateFlight = (serverLogout && this.refreshInFlight$ && !u.support) ? this.currentFlightId : null;
      const logoutReason = logoutReasonFor(o.kind);   // TC-0114: manual / idle / otherTab / expired

      this.sessionEpoch++;   // FIRST state change (DISC-009)
      this.refreshInFlight$ = null;
      this.lateLogoutFlightId = lateFlight;
      this.lateLogoutReason = logoutReason;
      this.refreshWhenOnline = false;
      this.lastTransient = null;
      this.clearRefreshTimers();
      this.stopSessionTracking();
      this.disconnectHub();

      if (serverLogout && logoutToken) this.sendServerLogout(logoutToken, logoutReason);

      const r = reasonFor(o.kind, { code: o.code, message: o.message ?? undefined, remoteReason: o.remoteReason, reason: o.reason });
      if (r) saveReason(r);

      // Broadcast while the session channel is still open.
      if (broadcast === 'user') {
        try { this.local?.setItem(signedOutKey(u.staffId), JSON.stringify({ at: Date.now(), reason: o.kind })); } catch {}
        const msg: UserLogoutMsg = { v: 2, type: 'logout', scope: 'user', staffId: u.staffId, reason: o.kind, message: r?.text ?? null };
        this.post(msg);
        this.postLegacy();
      } else if (broadcast === 'session' && chanSid) {
        this.postSession({ v: 2, type: 'logout', scope: 'session', reason: o.kind, code: o.code, message: r?.text ?? null });
      }

      this._localOnlyLogout();
      if (navigate) this.router.navigate(['/login']);
    } finally {
      this.ending = wasEnding;
    }
  }

  /** Tear down local session state without calling the API and without broadcasting. The last step of every
   *  sign-out (endSession) and of a failed start-up. */
  private _localOnlyLogout(): void {
    this.sessionEpoch++;
    this.peerSyncNeeded = false;
    this.peerSync$ = null;
    try { sessionStorage.removeItem(AUTH_KEY); } catch {}
    try { sessionStorage.removeItem(OWNER_STASH_KEY); } catch {}
    try { sessionStorage.removeItem(TAB_SEEN_KEY); } catch {}
    this.currentUserSubject.next(null);
    this.sessionTimeoutWarning$.next(null);
    this.idleWarningOnScreen = false;
    this.stopSessionTracking();
    this.clearRefreshTimers();
    this.closeSessionChannel();
    this.disconnectHub();
    this.setLocked(false);
    this.onSignedOutCleanup();
  }

  /** Nothing one person left on screen may be shown to the next person (B052). Each service is fetched lazily
   *  through the injector (as signalrService is), which avoids the HttpClient → interceptor → AuthService DI cycle. */
  private onSignedOutCleanup(): void {
    // DISC-012 / TC-5494: nothing saved from this person's API replies may reach the next person in this browser.
    // First: none of these can throw or wait.
    this.purgeSavedApiReplies();
    try { this.injector.get(ApiService).clearAllCache(); } catch {}
    // DISC-038 / TC-5494: also wipe the IndexedDB offline cache (billing / discharge / payment PHI)
    // so it cannot be read by the next person on a shared device. Fire-and-forget; never blocks sign-out.
    try { this.zone.runOutsideAngular(() => { void this.injector.get(OfflineCacheService).clearAllData(); }); } catch {}
    try {
      const local = this.local;
      if (local) for (let i = local.length - 1; i >= 0; i--) {
        const k = local.key(i);
        if (k && SHARED_PC_UI_CACHE_KEYS.some(base => k === base || k.startsWith(base + ':'))) local.removeItem(k);
      }
    } catch {}
    try { this.injector.get(NetworkGuardService).removeUserPanels(); } catch {}
    try { this.injector.get(DialogService).discardAll(); } catch {}
    try { this.injector.get(ToastService).dismissAll(); } catch {}
    this.lockState.clear();
  }

  /** DISC-012: removes API replies an older app version's service worker saved on this device. Every page load and every sign-out; never blocks, never throws. */
  private purgeSavedApiReplies(): void {
    try { this.zone.runOutsideAngular(() => { void purgeSwDataCaches(this.swCaches); }); } catch {}
  }

  private get lockState(): LockStateService {
    return this.injector.get(LockStateService);
  }

  /** The one place this service sets or clears the lock flag (L1's LockStateService owns it and the body class). */
  private setLocked(on: boolean): void {
    const ls = this.lockState;
    if (on) ls.lock(); else ls.clear();
  }

  private isLockedFlag(): boolean {
    try { return sessionStorage.getItem(LOCK_KEY) === '1'; } catch { return false; }
  }

  private disconnectHub(): void {
    try { this.signalrService.disconnect().catch(() => {}); } catch {}
  }

  // ── SERVER LOGOUT (fetch: no interceptor and no 'Save failed' toast; DISC-011) ──
  /** The token is always sent, even when locally expired: no device-clock dependence. `reason` (TC-0114) goes on the
   *  hospital's SIGNED_OUT audit row; every retry and the offline queue keep it. */
  private sendServerLogout(token: string, reason?: LogoutReason): void {
    this.zone.runOutsideAngular(() => {
      this.postLogout(token, true, reason)
        .then(status => this.afterLogoutStatus(token, status, reason))
        .catch(() => this.postLogout(token, false, reason)
          .then(status => this.afterLogoutStatus(token, status, reason))
          .catch(() => this.queuePendingLogout(token, reason)));
    });
  }

  private postLogout(token: string, keepalive: boolean, reason?: LogoutReason): Promise<number> {
    try {
      return fetch(`${this.apiUrl}/auth/logout`, {
        method: 'POST', keepalive, credentials: 'include',
        headers: { ...NGSW_BYPASS, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: reason ? JSON.stringify({ reason }) : '{}',
      }).then(r => r.status);
    } catch (e) { return Promise.reject(e); }
  }

  private static isTransientStatus(status: number): boolean {
    return status === 0 || status === 408 || status === 429 || status >= 500;
  }

  /** 200/401/403 are final; network or server trouble keeps the logout pending. */
  private afterLogoutStatus(token: string, status: number, reason?: LogoutReason): void {
    if (AuthService.isTransientStatus(status)) this.queuePendingLogout(token, reason);
  }

  private queuePendingLogout(token: string, reason?: LogoutReason): void {
    // After 15 min the server's idle rule has ended the session anyway.
    if (!this.pendingLogouts.some(p => p.token === token)) this.pendingLogouts.push({ token, dropAt: Date.now() + IDLE_TIMEOUT_MS, reason });
    if (!this.pendingFlushTimer) {
      this.pendingFlushTimer = this.zone.runOutsideAngular(() => setInterval(() => this.flushPendingLogouts(false), TRANSIENT_RETRY_MS));
    }
  }

  private flushPendingLogouts(keepalive: boolean): void {
    const now = Date.now();
    this.pendingLogouts = this.pendingLogouts.filter(p => p.dropAt > now);
    for (const p of [...this.pendingLogouts]) {
      this.postLogout(p.token, keepalive, p.reason)
        .then(status => { if (!AuthService.isTransientStatus(status)) this.removePendingLogout(p.token); })
        .catch(() => { /* keep it for the next try */ });
    }
    if (this.pendingLogouts.length === 0) this.stopPendingFlush();
  }

  private removePendingLogout(token: string): void {
    this.pendingLogouts = this.pendingLogouts.filter(p => p.token !== token);
    if (this.pendingLogouts.length === 0) this.stopPendingFlush();
  }

  private stopPendingFlush(): void {
    if (this.pendingFlushTimer) { clearInterval(this.pendingFlushTimer); this.pendingFlushTimer = null; }
  }

  // ── SHARED IDLE (one 15-min clock per person across this browser's tabs) ──
  private effectiveLastActivity(): number {
    const u = this.currentUser;
    return Math.max(this.lastActivityMs, u ? (readNum(this.local, activityKey(u.staffId)) ?? 0) : 0);
  }

  private recordActivity(ms: number): void {
    this.lastActivityMs = ms;
    this.lastActivityWriteMs = ms;
    const u = this.currentUser;
    if (u) { try { this.local?.setItem(activityKey(u.staffId), String(ms)); } catch {} }
  }

  /** DISC-024: true while the layout shows "Are you still there?" (set by the layout). Pages outside the layout
   *  (/change-password, a telemedicine room) show no dialog, so input there still counts during the last minute. */
  private idleWarningOnScreen = false;

  /** Called by the layout when its "Are you still there?" dialog appears and goes away. */
  setIdleWarningOnScreen(on: boolean): void { this.idleWarningOnScreen = on; }

  // ✅ USER ACTIVITY: real interactions only. Input on the lock overlay is not the signed-in person (TC-0166).
  private onUserActivity = (): void => {
    if (!this.isLoggedIn || this.isLockedFlag()) return;
    // DISC-024: while "Are you still there?" shows, only its own buttons answer it ("Stay logged in" →
    // resetSessionTimeout, "Logout now" → logout). Stray mouse moves, scrolls, taps or keys elsewhere neither count as
    // activity nor close it, so both buttons can be reached and a tap can't fall through to the page behind.
    // Another tab's activity still closes it (onStorage).
    if (this.idleWarningOnScreen && this.sessionTimeoutWarning$.value !== null) return;
    const now = Date.now();
    if (now - this.lastActivityWriteMs < ACTIVITY_THROTTLE_MS) return;
    this.recordActivity(now);
    this.armIdleTimers();
    this.hideWarning();
  };

  /** "Stay logged in" / unlock. A no-op while locked — L1's unlock clears the flag BEFORE calling this, so unlock
   *  still counts, but 'Stay logged in' behind a lock cannot keep a locked tab alive. */
  public resetSessionTimeout(): void {
    if (!this.isLoggedIn || this.isLockedFlag()) return;
    this.recordActivity(Date.now());
    this.armIdleTimers();
    this.hideWarning();
  }

  private hideWarning(): void {
    if (this.sessionTimeoutWarning$.value !== null) this.zone.run(() => this.sessionTimeoutWarning$.next(null));
  }

  private clearIdleTimers(): void {
    if (this.inactivityTimeout) { clearTimeout(this.inactivityTimeout); this.inactivityTimeout = null; }
    if (this.warningTimeout) { clearTimeout(this.warningTimeout); this.warningTimeout = null; }
    if (this.warningTickHandle) { clearInterval(this.warningTickHandle); this.warningTickHandle = null; }
  }

  private armIdleTimers(): void {
    this.clearIdleTimers();
    if (!this.isLoggedIn) return;
    const toEnd = IDLE_TIMEOUT_MS - (Date.now() - this.effectiveLastActivity());
    if (toEnd <= 0) { this.zone.run(() => this.endSession({ kind: 'idle' })); return; }
    this.zone.runOutsideAngular(() => {
      const toWarn = toEnd - WARNING_MS;
      if (toWarn <= 0) this.startWarning();
      else this.warningTimeout = setTimeout(() => this.onWarnTimer(), toWarn);
      this.inactivityTimeout = setTimeout(() => this.onIdleTimer(), toEnd);
    });
  }

  /** Re-checks the shared time first: another tab of this person may have been used meanwhile. */
  private onWarnTimer(): void {
    if (Date.now() - this.effectiveLastActivity() < IDLE_TIMEOUT_MS - WARNING_MS - 1000) this.armIdleTimers();
    else this.startWarning();
  }

  /** The 60…1 countdown of TC-0066, computed from the shared deadline. */
  private startWarning(): void {
    const emit = () => {
      const s = Math.max(1, Math.min(60, Math.ceil((this.effectiveLastActivity() + IDLE_TIMEOUT_MS - Date.now()) / 1000)));
      this.zone.run(() => this.sessionTimeoutWarning$.next(s));
    };
    emit();
    if (this.warningTickHandle) clearInterval(this.warningTickHandle);
    this.warningTickHandle = this.zone.runOutsideAngular(() => setInterval(emit, 1000));
  }

  private onIdleTimer(): void {
    if (Date.now() - this.effectiveLastActivity() < IDLE_TIMEOUT_MS - 1000) { this.hideWarning(); this.armIdleTimers(); }
    else this.zone.run(() => this.endSession({ kind: 'idle' }));
  }

  /** Covers sleep and throttled background timers (TC-0069). */
  private checkIdleNow(): void {
    if (this.isLoggedIn && Date.now() - this.effectiveLastActivity() >= IDLE_TIMEOUT_MS) {
      this.zone.run(() => this.endSession({ kind: 'idle' }));
    }
  }

  private startSessionTracking(): void {
    if (!this.activityListenerAttached && typeof window !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        for (const evt of this.activityEvents) window.addEventListener(evt, this.onUserActivity, { passive: true });
      });
      this.activityListenerAttached = true;
    }
    this.armIdleTimers();
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = this.zone.runOutsideAngular(() => setInterval(() => { this.writeTabSeen(); this.checkIdleNow(); }, HEARTBEAT_MS));
  }

  private stopSessionTracking(): void {
    if (this.activityListenerAttached && typeof window !== 'undefined') {
      for (const evt of this.activityEvents) window.removeEventListener(evt, this.onUserActivity);
      this.activityListenerAttached = false;
    }
    this.clearIdleTimers();
    if (this.watchdog) { clearInterval(this.watchdog); this.watchdog = null; }
    this.hideWarning();
  }

  private writeTabSeen(): void {
    if (this.isLoggedIn) { try { sessionStorage.setItem(TAB_SEEN_KEY, String(Date.now())); } catch {} }
  }

  /** AUDIT FIX (2026-06-16): public patch so flows like post-change-password update the in-memory subject
   *  AND sessionStorage together. Writing only sessionStorage left `currentUser` (which reads the subject)
   *  stale — e.g. mustChangePassword stayed true → the guard looped back to /change-password. */
  patchCurrentUser(patch: Partial<CurrentUser>): void {
    const cur = this.currentUserSubject.value;
    if (!cur) return;
    const updated = { ...cur, ...patch } as CurrentUser;
    sessionStorage.setItem(AUTH_KEY, JSON.stringify(updated));
    this.currentUserSubject.next(updated);
  }

  /** True while the server still requires a password change for this account (GET /auth/me is allowed while one is pending).
   *  If the server says it is no longer required (changed in another tab, or a renewal reply raced the change), clears the
   *  stale flag in this tab. Errors count as "still required". Callers at the same moment share one request. */
  recheckMustChangePassword(): Observable<boolean> {
    if (!this.mustChangeRecheck$) {
      // ngsw-bypass: never a service-worker cached reply (keyed by URL only — possibly another person's on a shared PC).
      this.mustChangeRecheck$ = this.http.get<any>(`${this.apiUrl}/auth/me`, { headers: NGSW_BYPASS }).pipe(
        map(res => res?.data?.mustChangePassword !== false),
        tap(still => { if (!still) this.patchCurrentUser({ mustChangePassword: false }); }),
        catchError(() => of(true)),
        finalize(() => { this.mustChangeRecheck$ = null; }),
        shareReplay(1));
    }
    return this.mustChangeRecheck$;
  }

  // ── GETTERS ───────────────────────────────
  get currentUser(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  get isLoggedIn(): boolean {
    return this.currentUserSubject.value !== null;
  }

  get token(): string {
    return this.currentUserSubject.value?.token ?? '';
  }

  get hospitalId(): number {
    return this.currentUserSubject.value?.hospitalId ?? 0;
  }

  get hospitalName(): string {
    return this.currentUserSubject.value?.hospitalName ?? '';
  }

  get role(): string {
    return this.currentUserSubject.value?.role ?? '';
  }

  get staffId(): number {
    return this.currentUserSubject.value?.staffId ?? 0;
  }

  get department(): string {
    return this.currentUserSubject.value?.department ?? '';
  }

  get fullName(): string {
    return this.currentUserSubject.value?.fullName ?? '';
  }

  // ── ROLE HELPERS ──────────────────────────
  isSuperAdmin(): boolean { return this.role === 'SuperAdmin'; }
  isHospitalAdmin(): boolean { return this.role === 'HospitalAdmin' || this.isSuperAdmin(); }
  isDoctor(): boolean { return this.role === 'Doctor'; }
  isReceptionist(): boolean { return this.role === 'Receptionist'; }
  isLab(): boolean { return this.role === 'Lab'; }
  isPharmacy(): boolean { return this.role === 'Pharmacy'; }
  isNurse(): boolean { return this.role === 'Nurse'; }
  isRadiology(): boolean { return this.role === 'Radiology'; }
  isBilling(): boolean { return this.role === 'Billing'; }

  canViewClinicalData(): boolean {
    return ['Doctor', 'Nurse', 'HospitalAdmin', 'SuperAdmin'].includes(this.role);
  }

  canManageBilling(): boolean {
    return ['Billing', 'Receptionist', 'HospitalAdmin', 'SuperAdmin'].includes(this.role);
  }

  canManageStaff(): boolean {
    return ['HospitalAdmin', 'SuperAdmin'].includes(this.role);
  }

  /** TC-0126: the signed-in role has a home page in this app (session-policy ROLE_HOME). */
  get hasKnownRole(): boolean { return isKnownRole(this.role); }

  // ── REDIRECT AFTER LOGIN ──────────────────
  redirectAfterLogin(): void {
  if (!this.isLoggedIn) { this.router.navigate(['/login']); return; }
  // TC-0126: an unknown role used to default to /receptionist-dashboard, whose guard sent it back here — a loop that
  // froze the tab. It is never redirected (not even to /change-password): the session ends instead.
  if (!this.hasKnownRole) { this.endInvalidAccount(); return; }
  // Force password change if required
  if (this.currentUser?.mustChangePassword) {
    this.router.navigate(['/change-password']);
    return;
  }
  this.router.navigate([ROLE_HOME[this.role]]);
}

  /**
   * TC-0126: this sign-in's role is not one this app knows (an old invite bug or a hand-edited row). Ends the session
   * the normal way (server logout included) and the sign-in page says "This account isn't set up correctly…".
   */
  endInvalidAccount(): void {
    this.endSession({ kind: 'forced', code: ACCOUNT_SETUP_INVALID, message: ACCOUNT_SETUP_INVALID_TEXT });
  }

  // ── STAFF MANAGEMENT ──────────────────────
  inviteStaff(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/invite-staff`, data, { headers: new HttpHeaders({ 'X-Timeout': '45000' }) });
  }

  getStaff(): Observable<any> {
    return this.http.get(`${this.apiUrl}/auth/staff`);
  }

  toggleStaffActive(staffId: number, active?: boolean): Observable<any> {
    return this.http.patch(`${this.apiUrl}/auth/staff/${staffId}/toggle-active`, active === undefined ? {} : { active });
  }

  resendInvite(staffId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/invite-staff/${staffId}/resend`, {}, { headers: new HttpHeaders({ 'X-Timeout': '45000' }) });
  }

  cancelInvite(staffId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/staff/${staffId}/cancel-invite`, {});
  }

  setPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/set-password`, { inviteToken: token, newPassword });
  }

  // ── PASSWORD MANAGEMENT ──────────────────
  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/forgot-password`, { email });
  }

  verifyOtpReset(email: string, otp: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/forgot-password/verify`, { email, otp, newPassword });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    // TC-0176: both pages (Change Password, Settings → Security) show the server's reply in their own box — no second,
    // global toast.
    return this.http.post(`${this.apiUrl}/auth/change-password`, { currentPassword, newPassword },
      { headers: new HttpHeaders({ 'X-No-Toast': 'true' }) });
  }

  adminResetPassword(staffId: number, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/admin-reset-password`, { staffId, newPassword });
  }

  // Admin clears a staff member's authenticator (MFA) enrollment — lost-phone recovery.
  resetStaffMfa(staffId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/staff/${staffId}/reset-mfa`, {});
  }

  /** DISC-015: a one-time code the admin gives in person; with the password it unlocks the authenticator QR once. */
  issueStaffSetupCode(staffId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/staff/${staffId}/mfa-setup-code`, {});
  }

  // ── HOSPITAL MANAGEMENT (SuperAdmin) ──────
  createHospital(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/hospitals`, data);
  }

  getHospitals(): Observable<any> {
    return this.http.get(`${this.apiUrl}/auth/hospitals`);
  }

  // ── TOKEN RENEWAL (one flight per tab) ─────────────────────────────

  /** Renew 5 minutes before the local expiry, plus 0-20 s jitter (spreads duplicated tabs that share one JWT exp). */
  private scheduleTokenRefresh(user: CurrentUser): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const delay = Math.max(tokenExpMs(user) - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS)
      + Math.floor(Math.random() * REFRESH_JITTER_MS);
    console.log(`🔄 Token refresh scheduled in ${Math.round(delay / 60000)} min`);
    const due = Date.now() + delay;
    this.refreshTimer = this.zone.runOutsideAngular(() => setTimeout(() => {
      // Far later than planned: the tab was frozen or suspended and may have missed another tab's rotation.
      if (Date.now() - due > SUSPENDED_GAP_MS) this.peerSyncNeeded = true;
      this.zone.run(() => this.onRefreshTimer());
    }, delay));
  }

  private clearRefreshTimers(): void {
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
    if (this.refreshRetryTimer) { clearTimeout(this.refreshRetryTimer); this.refreshRetryTimer = null; }
  }

  /** Renewal is not activity: an idle person is signed out instead of renewed (TC-0015). */
  private onRefreshTimer(): void {
    if (!this.isLoggedIn) return;
    if (Date.now() - this.effectiveLastActivity() >= IDLE_TIMEOUT_MS) { this.endSession({ kind: 'idle' }); return; }
    if (!navigator.onLine) { this.refreshWhenOnline = true; return; }
    this.refreshSession('timer').subscribe();
  }

  private scheduleRefreshRetry(delayMs: number = TRANSIENT_RETRY_MS): void {
    if (this.refreshRetryTimer) clearTimeout(this.refreshRetryTimer);
    this.refreshRetryTimer = this.zone.runOutsideAngular(() =>
      setTimeout(() => this.zone.run(() => this.onRefreshTimer()), delayMs));
  }

  /**
   * THE single renewal point (TC-0058/0059): every trigger (timer, 401, start-up, unlock, support) shares the flight
   * in progress. A refused renewal (4xx) ends the session exactly once; network or server trouble keeps it and
   * retries after 30 s or when back online. A 429 is trouble too, never a sign-out: it retries after the server's
   * Retry-After (at most 60 s) — R1 (TC-0009).
   */
  public refreshSession(trigger: RefreshTrigger): Observable<RefreshResult> {
    if (this.refreshInFlight$) return this.refreshInFlight$;
    const u = this.currentUser;
    if (!u) return of({ ok: false, terminal: true, code: 'NO_SESSION' } as RefreshResult);
    // No ghost renewal after sleep.
    if ((trigger === '401' || trigger === 'unlock') && Date.now() - this.effectiveLastActivity() >= IDLE_TIMEOUT_MS) {
      this.endSession({ kind: 'idle' });
      return of({ ok: false, terminal: true, code: 'IDLE' } as RefreshResult);
    }
    // Back-off: 401-triggered renewals within 30 s (a 429: its wait) of a transient failure reuse it ('timer'/'startup' always post).
    if ((trigger === '401' || trigger === 'unlock') && this.lastTransient
        && Date.now() - this.lastTransient.at < (this.lastTransient.waitMs ?? TRANSIENT_RETRY_MS)) {
      return of(this.lastTransient.result);
    }

    const owner = tokenIdentity(u.token);
    const flightId = ++this.flightSeq;

    // Body mode: a tab that may have missed a rotation first gets the current pair from the live tabs of its session,
    // so it never sends a refresh token that was already rotated (S1 reuse detection would end the shared session).
    const sync$ = this.refreshMode === 'body' && (this.peerSyncNeeded || this.peerSync$) ? this.peerSyncOnce$() : null;
    const source$: Observable<RefreshResult> = sync$
      ? sync$.pipe(switchMap(() => defer(() => this.afterPeerSync$(u, owner, trigger, flightId))))
      : this.renewal$(u, this.sessionEpoch, owner, trigger, flightId);

    const flight: Observable<RefreshResult> = source$.pipe(
      defaultIfEmpty({ ok: false, terminal: false, code: 'NO_RESULT' } as RefreshResult),
      finalize(() => { if (this.refreshInFlight$ === flight) this.refreshInFlight$ = null; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.refreshInFlight$ = flight;
    this.currentFlightId = flightId;
    flight.subscribe();
    return flight;
  }

  /** The renewal itself: support token, no way to renew, or POST /auth/refresh with this tab's pair. */
  private renewal$(u: CurrentUser, epoch: number, owner: Identity | null, trigger: RefreshTrigger, flightId: number): Observable<RefreshResult> {
    if (u.support) return this.renewSupportCore$(u, epoch, owner);
    if (!this.canRefresh) {
      return defer(() => {
        this.endSession({ kind: 'forced', code: 'NO_REFRESH', navigate: trigger !== 'startup' });
        return of({ ok: false, terminal: true, code: 'NO_REFRESH' } as RefreshResult);
      });
    }
    return this.postRefresh(u).pipe(
      map((res: any): RefreshResult => {
        if (!(res?.success && res.data?.token)) {
          throw new HttpErrorResponse({ status: 401, error: { code: 'REFRESH_FAILED', message: res?.message } });
        }
        const nu = this.applyRefreshed(res.data, epoch);
        if (nu) return { ok: true, user: nu };
        // Signed out while this renewal was in flight: the server just minted a token for an ended session —
        // use it once to log that session out for real (DISC-009, server side).
        if (this.lateLogoutFlightId === flightId) {
          this.lateLogoutFlightId = null;
          this.sendServerLogout(res.data.token, this.lateLogoutReason);
        }
        return this.staleResult(owner);
      }),
      catchError(err => this.onRefreshError(err, epoch, owner, trigger)),
    );
  }

  /** After a peer sync: signed out meanwhile → stale; a live tab handed over a newer pair that is still good → use it
   *  with no server call; otherwise renew with the pair this tab holds NOW. */
  private afterPeerSync$(sent: CurrentUser, owner: Identity | null, trigger: RefreshTrigger, flightId: number): Observable<RefreshResult> {
    const cur = this.currentUser;
    if (!cur || !sameIdentity(tokenIdentity(cur.token), owner)) return of(this.staleResult(owner));
    if (cur.token !== sent.token && tokenExpMs(cur) - Date.now() > 60_000) return of({ ok: true, user: cur } as RefreshResult);
    return this.renewal$(cur, this.sessionEpoch, owner, trigger, flightId);
  }

  /** Body mode: marks this tab as possibly holding a rotated-away pair and asks the live tabs of its session at once. */
  private requestPeerSync(): void {
    if (this.refreshMode !== 'body' || !this.isLoggedIn) return;
    this.peerSyncNeeded = true;
    this.peerSyncOnce$();
  }

  /** Posts 'sync' on this session's channel and waits up to PEER_SYNC_WAIT_MS for a live tab's newer pair (it ends at
   *  the first adoption). One at a time; with no channel it completes at once. Never fails. */
  private peerSyncOnce$(): Observable<void> {
    if (this.peerSync$) return this.peerSync$;
    if (!this.sessionChannel || !this.isLoggedIn) { this.peerSyncNeeded = false; return of(undefined); }
    const sync$: Observable<void> = new Observable<void>(sub => {
      const done = () => { if (!sub.closed) { sub.next(); sub.complete(); } };
      const adopted = this.peerAdopted$.subscribe(done);
      const t = this.zone.runOutsideAngular(() => setTimeout(() => this.zone.run(done), PEER_SYNC_WAIT_MS));
      this.postSession({ v: 2, type: 'sync' });
      return () => { adopted.unsubscribe(); clearTimeout(t); };
    }).pipe(
      finalize(() => { if (this.peerSync$ === sync$) { this.peerSync$ = null; this.peerSyncNeeded = false; } }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.peerSync$ = sync$;
    sync$.subscribe();
    return sync$;
  }

  /** The 'rotated' message for a pair: the refresh token only in body mode; expLocalMs is this device's local expiry. */
  private rotatedMsg(u: CurrentUser): SessionMsg {
    return {
      v: 2, type: 'rotated', token: u.token, expiry: u.expiry, expLocalMs: tokenExpMs(u),
      ...(this.refreshMode === 'body' && u.refreshToken ? { refreshToken: u.refreshToken } : {}),
    };
  }

  private postRefresh(u: { token: string; refreshToken?: string }): Observable<any> {
    const cookie = this.refreshMode === 'cookie';
    const headers: Record<string, string> = { ...NGSW_BYPASS, ...(cookie ? { 'X-Refresh-Mode': 'cookie' } : {}) };
    const body = cookie ? { token: u.token } : { token: u.token, refreshToken: u.refreshToken };
    return this.bareHttp.post<any>(`${this.apiUrl}/auth/refresh`, body, { headers, withCredentials: true })
      .pipe(timeout(REFRESH_TIMEOUT_MS));
  }

  private onRefreshError(err: unknown, epoch: number, owner: Identity | null, trigger: RefreshTrigger): Observable<RefreshResult> {
    // Never sign out on a stale callback (see sessionEpoch).
    if (this.sessionEpoch !== epoch) return of(this.staleResult(owner));
    const c = classifyRefreshError(err);
    console.warn('Session renewal failed', c.status, c.code);   // never log the body
    if (c.terminal) {
      this.lastTransient = null;
      this.endSession({ kind: 'forced', code: c.code, message: c.message, reason: c.reason, navigate: trigger !== 'startup' });
      return of({ ok: false, terminal: true, status: c.status, code: c.code, message: c.message } as RefreshResult);
    }
    const result: RefreshResult = { ok: false, terminal: false, status: c.status, code: c.code, message: c.message, error: err };
    const waitMs = transientRetryMs(err);
    this.lastTransient = { at: Date.now(), result, waitMs };
    if (navigator.onLine) this.scheduleRefreshRetry(waitMs); else this.refreshWhenOnline = true;
    return of(result);
  }

  /** The same person and session (e.g. after a 'rotated' adoption) get the current token; anyone else gets nothing. */
  private staleResult(owner: Identity | null): RefreshResult {
    const cur = this.currentUser;
    return cur && sameIdentity(tokenIdentity(cur.token), owner)
      ? { ok: true, user: cur }
      : { ok: false, terminal: true, code: 'ENDED' };
  }

  /** Applies a renewal reply unless this tab signed out / in meanwhile. A renewal is NOT activity. */
  private applyRefreshed(data: any, epoch: number): CurrentUser | null {
    const prev = this.currentUser;
    if (this.sessionEpoch !== epoch || !prev) return null;
    if (String(tokenIdentity(data?.token)?.staffId) !== String(prev.staffId)) return null;
    const nu = this.normalise(data, prev);
    this.sessionEpoch++;
    this.lastTransient = null;
    this.persist(nu);
    this.currentUserSubject.next(nu);
    this.scheduleTokenRefresh(nu);
    if (this.sessionId) this.postSession(this.rotatedMsg(nu));
    // A hub start that failed for lack of a valid token is retried now (DISC-007 / TC-0004).
    setTimeout(() => { try { this.signalrService.restartIfDown().catch(() => {}); } catch {} }, 0);
    return nu;
  }

  /** Support tab: renew the support token with the owner's kept-aside sign-in, while the hospital's approval lasts. */
  private renewSupportCore$(cur: CurrentUser, epoch: number, owner: Identity | null): Observable<RefreshResult> {
    const s = cur.support!;
    const stash = this.readStash();
    if (!stash) {
      return defer(() => {
        this.endSession({ kind: 'forced', message: SUPPORT_ENDED_TEXT, fromSupportExit: true });
        return of({ ok: false, terminal: true, code: 'NO_OWNER' } as RefreshResult);
      });
    }
    // The owner's stash is renewed the same way as any sign-in; the rotation goes on the owner's session channel so
    // the owner's other tabs (duplicated console) adopt it.
    const ownerToken$: Observable<CurrentUser> = tokenExpMs(stash) - Date.now() > 60_000
      ? of(stash)
      : this.postRefresh(stash).pipe(
          map((res: any) => {
            if (!(res?.success && res.data?.token)) throw new Error('owner sign-in expired');
            const o = this.normalise(res.data, stash);
            try { sessionStorage.setItem(OWNER_STASH_KEY, JSON.stringify(o)); } catch {}
            this.postSession(this.rotatedMsg(o));
            return o;
          }),
          catchError(e => throwError(() => ({ ownerStep: true, e }))),
        );
    return ownerToken$.pipe(
      // X-Owner-Auth: the interceptor keeps this Authorization header (the owner's) instead of the support token.
      // OWNER_AUTH_REQUEST: its refusals come back here (SessionTimeoutInterceptor does not sign out on its own).
      switchMap(o => this.http.post<any>(`${this.apiUrl}/platform/hospitals/${s.hospitalId}/support-requests/${s.grantId}/session`, {},
        { headers: { Authorization: `Bearer ${o.token}`, 'X-Owner-Auth': '1' }, context: new HttpContext().set(OWNER_AUTH_REQUEST, true) })),
      map((sess: any): RefreshResult => {
        if (this.sessionEpoch !== epoch || !this.currentUser?.support) return this.staleResult(owner);
        const updated: CurrentUser = {
          ...cur, token: sess.token, expiry: sess.expiry, expLocalMs: localExpiryMs(sess.token, Date.now()),
          hospitalAccess: sess.hospitalAccess, support: { ...s, scope: sess.scope, endsAt: sess.endsAt },
        };
        this.sessionEpoch++;
        this.persist(updated);
        this.currentUserSubject.next(updated);
        this.scheduleTokenRefresh(updated);
        return { ok: true, user: updated };
      }),
      catchError((x: any): Observable<RefreshResult> => {
        if (this.sessionEpoch !== epoch) return of(this.staleResult(owner));
        const err = x?.ownerStep ? x.e : x;
        const c = classifyRefreshError(err);
        if (c.terminal) {
          this.lastTransient = null;
          // The owner's own sign-in is dead → /login with the reason: its renewal failed (ownerStep), or the server
          // refused the owner's token on the support-session call (C1 item 9). Otherwise only the support access ended
          // (400 from the support-access check) → back to the owner console with the notice.
          if (x?.ownerStep || AuthService.ownerSignInRefused(err)) {
            this.endSession({ kind: 'forced', code: c.code, message: c.message, reason: c.reason, fromSupportExit: true });
          } else this.endSession({ kind: 'forced', message: SUPPORT_ENDED_TEXT });
          return of({ ok: false, terminal: true, status: c.status, code: c.code, message: c.message } as RefreshResult);
        }
        const result: RefreshResult = { ok: false, terminal: false, status: c.status, code: c.code, message: c.message, error: err };
        this.lastTransient = { at: Date.now(), result };
        if (navigator.onLine) this.scheduleRefreshRetry(); else this.refreshWhenOnline = true;
        return of(result);
      }),
    );
  }

  /** A refusal of the OWNER's own token on the support-session call (sent with the owner's token, so the support
   *  checks never answer it with 401): any 401 — session ended, password changed, switched off (TOKEN_REVOKED /
   *  ACCOUNT_DEACTIVATED), idle (SESSION_EXPIRED) — or a 403 password-change requirement. */
  private static ownerSignInRefused(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    const body = err.error && typeof err.error === 'object' ? err.error : null;
    if (err.status === 401) return body?.code !== 'SUPPORT_SESSION_ENDED';
    return err.status === 403 && body?.mustChangePassword === true;
  }

  /** A token good for at least 30 s more, renewing first if needed; null when the session cannot continue. */
  public async getValidAccessToken(): Promise<string | null> {
    const u = this.currentUser;
    if (!u) return null;
    if (tokenExpMs(u) - Date.now() > 30_000) return u.token;
    const r = await firstValueFrom(this.refreshSession('unlock'), { defaultValue: { ok: false, terminal: false } as RefreshResult });
    return r.ok ? r.user.token : null;
  }

  /** @deprecated wrapper kept for L1's onUnlock — use refreshSession('unlock'). */
  public refreshAndRetry(): Observable<CurrentUser | null> {
    return this.refreshSession('unlock').pipe(map(r => (r.ok ? r.user : null)));
  }

  // ── SUPPORT SESSIONS (owner works inside a hospital with its approval) ─────────────
  // The owner's own sign-in is kept aside in this tab only; the tab then runs on a short-lived support token for
  // that one hospital. Switching either way reloads the app so no screen keeps data from the other identity.

  get inSupportSession(): boolean { return !!this.currentUserSubject.value?.support; }
  get supportInfo(): SupportInfo | null { return this.currentUserSubject.value?.support ?? null; }

  /** Owner: switch this tab into an approved support session (from POST …/support-requests/{id}/session). */
  enterSupportSession(session: any): void {
    const owner = this.currentUserSubject.value;
    if (!owner || owner.role !== 'SuperAdmin' || owner.support) return;
    // The stash keeps the owner as stored (signedInAt, sessionId, expLocalMs).
    sessionStorage.setItem(OWNER_STASH_KEY, JSON.stringify(owner));
    const user: CurrentUser = {
      token: session.token, fullName: session.fullName, email: session.email, role: session.role,
      department: null, staffId: session.staffId, hospitalId: session.hospitalId, hospitalName: session.hospitalName,
      hospitalLogo: null, expiry: session.expiry, hospitalAccess: session.hospitalAccess, hospitalAccessReason: null,
      signedInAt: owner.signedInAt ?? Date.now(), sessionId: null, expLocalMs: localExpiryMs(session.token, Date.now()),
      support: { grantId: session.grantId, hospitalId: session.hospitalId, hospitalName: session.hospitalName, scope: session.scope, endsAt: session.endsAt },
    };
    sessionStorage.setItem(AUTH_KEY, JSON.stringify(user));
    this.clearRefreshTimers();
    this.disconnectHub();
    this.navigateHard('/dashboard');
  }

  /**
   * Leave the support session and go back to the owner console. `message` = why it ended on its own (then the
   * server is not called — the approval is already over); without it the owner chose to finish, and the approval
   * is ended on the server so it can't be picked up again.
   */
  exitSupportSession(message?: string): void {
    if (this.exitingSupport) return;
    this.exitingSupport = true;
    const cur = this.currentUserSubject.value;
    const s = cur?.support;
    const finish = () => {
      let stash: string | null = null;
      try { stash = sessionStorage.getItem(OWNER_STASH_KEY); sessionStorage.removeItem(OWNER_STASH_KEY); } catch {}
      this.clearRefreshTimers();
      this.disconnectHub();
      if (!stash) { this.endSession({ kind: 'forced', message, fromSupportExit: true }); this.exitingSupport = false; return; }
      sessionStorage.setItem(AUTH_KEY, stash);
      if (message) { try { sessionStorage.setItem('mt_support_notice', message); } catch { /* ignore */ } }
      this.navigateHard(s ? `/super-admin/hospitals/${s.hospitalId}` : '/super-admin');
    };
    if (!message && s && cur?.token && tokenExpMs(cur) > Date.now()) {
      this.http.post(`${this.apiUrl}/support-session/end`, {}).subscribe({ complete: finish, error: finish });
    } else finish();
  }

  /** Test seam: a full page load (support sessions switch identity by reloading). */
  private navigateHard(url: string): void {
    window.location.assign(url);
  }

  // ── CROSS-TAB ─────────────────────────────────────────────────────
  private post(msg: UserLogoutMsg): void {
    try { this.sharedChannel?.postMessage(msg); } catch {}
  }

  /** REMOVE one release after C1 — lets old-bundle tabs sign out */
  private postLegacy(): void {
    try { this.sharedChannel?.postMessage(LEGACY_LOGOUT); } catch {}
  }

  private postSession(msg: SessionMsg): void {
    try { this.sessionChannel?.postMessage(msg); } catch {}
  }

  /** only tabs that already hold this session's token can name this channel — tokens never reach another person's tab */
  private syncSessionChannel(): void {
    const want = this.isLoggedIn ? this.channelSid : null;
    if (want === this.sessionChannelSid) return;
    this.closeSessionChannel();
    if (!want) return;
    this.zone.runOutsideAngular(() => {
      this.sessionChannel = this.channelFactory(sessionChannelName(want));
      this.sessionChannelSid = this.sessionChannel ? want : null;
      if (this.sessionChannel) this.sessionChannel.onmessage = e => this.handleSessionMessage(e.data, want);
    });
  }

  private closeSessionChannel(): void {
    try { this.sessionChannel?.close(); } catch {}
    this.sessionChannel = null;
    this.sessionChannelSid = null;
  }

  /** Shared channel: only a v2 user-scope logout for this person is honoured (DISC-003). Every receiving tab ends
   *  ITS OWN server session (idempotent for a same-session tab). */
  public handleSharedMessage(data: unknown): void {
    if (!this.isLoggedIn) return;
    const u = this.currentUser!;
    if (!shouldHonourUserLogout(data, u.staffId)) return;
    const msg = data as UserLogoutMsg;
    // TC-0068 belt and braces: a forgotten idle tab never signs out a tab where the person is working.
    if (msg.reason === 'idle' && Date.now() - this.effectiveLastActivity() < IDLE_TIMEOUT_MS - 5000) return;
    this.zone.run(() => this.endSession({
      kind: 'remote', remoteReason: msg.reason, message: msg.message,
      serverLogout: msg.reason !== 'everywhere', broadcast: 'none',
    }));
  }

  /** Per-session channel: server-forced logouts, 'rotated' tokens and 'sync' requests of this device session. */
  public handleSessionMessage(data: any, chanSid: string): void {
    if (!this.isLoggedIn || data?.v !== 2) return;
    if (data.type === 'logout') {
      this.zone.run(() => this.endSession({
        kind: 'remote', remoteReason: data.reason, message: data.message,
        serverLogout: data.reason !== 'everywhere', broadcast: 'none',
      }));
      return;
    }
    if (data.type === 'sync') { this.answerPeerSync(chanSid); return; }
    if (data.type !== 'rotated' || typeof data.token !== 'string') return;
    const idt = tokenIdentity(data.token);
    const cur = this.currentUser!;
    const now = Date.now();
    // A sync answer may be about a token issued minutes ago: take the sender's local expiry (same device clock).
    const expLocalMs = typeof data.expLocalMs === 'number' && Number.isFinite(data.expLocalMs)
      ? data.expLocalMs : localExpiryMs(data.token, now);
    if (cur.support) {
      // The target is the owner stash.
      const stash = this.readStash();
      if (!stash || !idt || idt.sid !== chanSid || idt.staffId !== String(stash.staffId)
          || (jwtExpMs(data.token) ?? 0) <= (jwtExpMs(stash.token) ?? 0)) return;
      const o: CurrentUser = {
        ...stash, token: data.token, expiry: data.expiry, expLocalMs,
        ...(data.refreshToken && this.refreshMode === 'body' ? { refreshToken: data.refreshToken } : {}),
      };
      try { sessionStorage.setItem(OWNER_STASH_KEY, JSON.stringify(o)); } catch {}
      this.zone.run(() => this.peerAdopted$.next());
      return;
    }
    if (!idt || idt.sid !== this.sessionId || idt.staffId !== String(cur.staffId) || idt.grant !== null
        || (jwtExpMs(data.token) ?? 0) <= (jwtExpMs(cur.token) ?? 0)) return;
    const nu: CurrentUser = {
      ...cur, token: data.token, expiry: data.expiry, expLocalMs,
      ...(data.refreshToken && this.refreshMode === 'body' ? { refreshToken: data.refreshToken } : {}),
    };
    // Any own in-flight renewal then counts as stale, and its waiters get this token through staleResult.
    this.zone.run(() => {
      this.sessionEpoch++;
      this.persist(nu);
      this.currentUserSubject.next(nu);
      this.scheduleTokenRefresh(nu);
      this.peerAdopted$.next();
    });
  }

  /** Another tab of this session asks for the current pair: answer with this tab's as a normal 'rotated' (it is
   *  adopted only when newer than what that tab holds). A support tab answers with the owner's kept-aside pair. */
  private answerPeerSync(chanSid: string): void {
    const cur = this.currentUser;
    if (!cur || this.ending) return;
    const pair = cur.support ? this.readStash() : cur;
    if (!pair || (jwtSid(pair.token) ?? pair.sessionId ?? null) !== chanSid) return;
    this.postSession(this.rotatedMsg(pair));
  }

  private onStorage(e: StorageEvent): void {
    const u = this.currentUser;
    if (!u || !e.key) return;
    if (e.key === activityKey(u.staffId)) { this.armIdleTimers(); this.hideWarning(); return; }
    // Fallback ONLY for browsers without BroadcastChannel; newValue null (housekeeping) is ignored.
    if (e.key === signedOutKey(u.staffId) && !this.sharedChannel && e.newValue) {
      const m = parseSignedOut(e.newValue);
      if (m && u.signedInAt != null && m.at >= u.signedInAt) {
        this.zone.run(() => this.endSession({
          kind: 'remote', remoteReason: m.reason, serverLogout: m.reason !== 'everywhere', broadcast: 'none',
        }));
      }
    }
  }

  private onOnline(): void {
    this.flushPendingLogouts(false);
    if (this.refreshWhenOnline) {
      this.refreshWhenOnline = false;
      this.zone.run(() => this.onRefreshTimer());
    }
  }

  /** Back/forward cache restore: the same rules as start-up (a tab away for over 30 s comes back locked). */
  private onPageRestored(): void {
    const u = this.currentUser;
    if (!u) return;
    const d = this.startupDecision(u, false);
    if (d === 'end-signed-out') { this.zone.run(() => this.endSignedOutInBrowser(u, true)); return; }
    if (d === 'end-idle' || d === 'end-stale') {
      this.zone.run(() => this.endSession({ kind: d === 'end-idle' ? 'idle' : 'stale', broadcast: 'none' }));
      return;
    }
    if (d === 'resume-locked') {
      this.setLocked(true);
      this.zone.run(() => {
        this.lockRequested$.next();
        // Only LayoutComponent draws the lock. A page outside the app shell (/change-password, /tele) has nobody
        // listening, and a cache restore runs no route guard, so it would stay usable while flagged locked: open the
        // role's home page inside the layout, which shows the lock (as lockedRouteGuard does for a typed address).
        // In the forced-change case this is /change-password itself, which stays reachable (lockedRouteGuard).
        if (!this.lockRequested$.observed) this.redirectAfterLogin();
      });
    }
    this.writeTabSeen();
    // Frozen in the cache while other tabs may have rotated: get the current pair before the next renewal.
    this.requestPeerSync();
  }

  /** Purges this browser's per-person markers older than 24 h (staff ids and times only). */
  private housekeep(): void {
    try {
      const ls = this.local;
      if (!ls) return;
      const cutoff = Date.now() - HOUSEKEEP_AFTER_MS;
      const keys: string[] = [];
      for (let i = 0; i < ls.length; i++) { const k = ls.key(i); if (k) keys.push(k); }
      for (const k of keys) {
        if (k.startsWith('mt_act_')) {
          if (Number(ls.getItem(k)) < cutoff) ls.removeItem(k);
        } else if (k.startsWith('mt_signout_')) {
          const m = parseSignedOut(ls.getItem(k));
          if (m && m.at < cutoff) ls.removeItem(k);
        }
      }
    } catch {}
  }
}
